import { useEffect, useState, useSyncExternalStore } from "react";
import { useLocale, useT } from "@shared/client/locale";
import { legalUrl } from "@shared/lib/legal";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { Switch } from "@shared/components/ui/switch";
import { forgetAnalyticsIds } from "@shared/lib/analytics-client";
import {
  DEFAULT_CHOICES,
  GA_MEASUREMENT_ID,
  currentConsent,
  onConsentPreferencesRequested,
  parseConsent,
  readConsentCookie,
  saveConsent,
  subscribeConsent,
  type ConsentChoices,
} from "@shared/lib/consent";

/** Valeur brute du cookie ; null pendant le rendu serveur et l'hydratation. */
export function useConsentCookie() {
  return useSyncExternalStore(subscribeConsent, readConsentCookie, () => null);
}

function savedChoices(): ConsentChoices {
  const saved = currentConsent();
  return saved ? { analytics: saved.analytics, internalStats: saved.internalStats } : DEFAULT_CHOICES;
}

function persist(choices: ConsentChoices) {
  if (!choices.internalStats) forgetAnalyticsIds();
  saveConsent(choices);
}

/**
 * Bandeau cookies et panneau « Gestion des cookies ».
 *
 * Conforme aux recommandations de la CNIL : refuser est aussi simple
 * qu'accepter (deux boutons identiques), rien n'est déposé avant le choix, et
 * le panneau se rouvre à tout moment depuis les pieds de page. Le bandeau ne
 * s'affiche que si un traceur soumis à consentement existe (Google Analytics
 * configuré) ; le panneau, lui, reste accessible pour s'opposer aux
 * statistiques internes.
 */
export function CookieConsent() {
  const raw = useConsentCookie();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ConsentChoices>(DEFAULT_CHOICES);
  const copy = useT().common.consent;
  const locale = useLocale();

  useEffect(
    () =>
      onConsentPreferencesRequested(() => {
        setDraft(savedChoices());
        setOpen(true);
      }),
    [],
  );

  const showBanner = Boolean(GA_MEASUREMENT_ID) && raw !== null && !parseConsent(raw) && !open;

  function choose(choices: ConsentChoices) {
    persist(choices);
    setOpen(false);
  }

  function customize() {
    setDraft(savedChoices());
    setOpen(true);
  }

  return (
    <>
      {showBanner && (
        <section
          aria-describedby="cookie-banner-text"
          aria-labelledby="cookie-banner-title"
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-[980px] rounded-xl border bg-white p-5 shadow-[0_18px_50px_-20px_rgba(10,10,30,.35)] sm:inset-x-5 sm:bottom-5"
          role="region"
        >
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-8">
            <div>
              <h2 className="m-0 text-[14px] font-semibold" id="cookie-banner-title">{copy.bannerTitle}</h2>
              <p className="mt-1.5 mb-0 text-[13px] leading-relaxed text-muted-foreground" id="cookie-banner-text">
                {copy.bannerText}{" "}
                <a className="text-primary underline underline-offset-2" href={legalUrl("privacy", locale, "#cookies")}>
                  {copy.privacyLink}
                </a>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
              <Button className="order-last col-span-2 sm:order-none" onClick={customize} variant="outline">
                {copy.customize}
              </Button>
              <Button onClick={() => choose({ analytics: false, internalStats: true })}>{copy.refuseAll}</Button>
              <Button onClick={() => choose({ analytics: true, internalStats: true })}>{copy.acceptAll}</Button>
            </div>
          </div>
        </section>
      )}

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent className="max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{copy.settings}</DialogTitle>
            <DialogDescription className="leading-relaxed">{copy.panelText}</DialogDescription>
          </DialogHeader>

          <div className="grid border-t">
            <ConsentRow
              description={copy.necessaryText}
              title={copy.necessary}
            >
              <span className="text-[12.5px] whitespace-nowrap text-muted-foreground">{copy.alwaysOn}</span>
            </ConsentRow>
            <ConsentRow
              description={copy.internalStatsText}
              id="consent-internal-stats"
              title={copy.internalStats}
            >
              <Switch
                aria-labelledby="consent-internal-stats"
                checked={draft.internalStats}
                onCheckedChange={(internalStats) => setDraft((current) => ({ ...current, internalStats }))}
              />
            </ConsentRow>
            {GA_MEASUREMENT_ID && (
              <ConsentRow
                description={copy.analyticsText}
                id="consent-analytics"
                title="Google Analytics"
              >
                <Switch
                  aria-labelledby="consent-analytics"
                  checked={draft.analytics}
                  onCheckedChange={(analytics) => setDraft((current) => ({ ...current, analytics }))}
                />
              </ConsentRow>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => choose({ analytics: false, internalStats: false })} variant="outline">
              {copy.refuseAll}
            </Button>
            <Button onClick={() => choose(draft)}>{copy.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ConsentRow({
  id,
  title,
  description,
  children,
}: {
  id?: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6 border-b py-4">
      <div>
        <p className="m-0 text-[13.5px] font-medium" id={id}>{title}</p>
        <p className="mt-1 mb-0 text-[13px] leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <div className="pt-0.5">{children}</div>
    </div>
  );
}
